import type { ComponentType } from "react";
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams
} from "react-router";
import { isSameLocation } from "./sameLocation";

interface RouteElementProps {
  Component: ComponentType<any>;
  path?: string;
  [key: string]: unknown;
}

const basePattern = (path?: string) => path?.replace(/\/\*$/, "") ?? "";

const buildMatchedUrl = (
  path: string | undefined,
  params: Record<string, string | undefined>,
  pathname: string
) => {
  const pattern = basePattern(path);
  if (!pattern) return pathname;

  try {
    return generatePath(pattern, params);
  } catch {
    return pattern || pathname;
  }
};

export const RouteElement = ({
  Component,
  path,
  ...props
}: RouteElementProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const match = {
    params,
    path: basePattern(path),
    url: buildMatchedUrl(path, params, location.pathname),
    isExact: true
  };
  const history = {
    location,
    push: (to: any) => {
      if (!isSameLocation(to, location)) navigate(to);
    },
    replace: (to: any) => {
      if (!isSameLocation(to, location)) navigate(to, { replace: true });
    },
    goBack: () => navigate(-1),
    go: (delta: number) => navigate(delta)
  };

  return (
    <Component {...props} match={match} location={location} history={history} />
  );
};

export const withRouteData = <P extends object>(
  Component: ComponentType<P>
) => {
  const WrappedRouteData = (
    props: Omit<P, "match" | "location" | "history">
  ) => {
    if (
      (props as any).match &&
      (props as any).location &&
      (props as any).history
    ) {
      return <Component {...(props as P)} />;
    }

    return (
      <RouteElement Component={Component as ComponentType<any>} {...props} />
    );
  };

  WrappedRouteData.displayName = `withRouteData(${Component.displayName || Component.name || "Component"})`;

  return WrappedRouteData;
};
